/**
 * DocumentaryPage ✦ — iter206
 * ─────────────────────────────────────────────────────────────
 * Operator-only surface for The Documentary ✦. The brief locks
 * the entire surface contract:
 *
 *   • One line of status copy
 *   • One "Regenerate ✦" gesture
 *   • One video preview when settled
 *
 *   NO editing controls.
 *   NO scene controls.
 *   NO music pickers.
 *   NO timeline UI.
 *   NO export settings.
 *   NO progress bar.
 *
 * The Documentary must never feel "on demand". Even when the encode
 * is fast, the surface holds the gathering line until the artefact
 * has settled. The page reads as patient, archival, and authored —
 * not as a generation interface.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import BackButton from '../components/BackButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INK = '#1f1409';
const INK_SOFT = 'rgba(31,20,9,0.72)';
const INK_FAINT = 'rgba(31,20,9,0.55)';
const PARCHMENT = '#f5efdf';
const IVORY = '#fbf6ec';
const GOLD = '#a4824a';
const RULE = 'rgba(31,20,9,0.14)';
const FONT_DISPLAY = "'Cormorant Garamond','Playfair Display',Georgia,serif";
const FONT_BODY = "'Inter',system-ui,sans-serif";

const PHASE_LABELS = {
  before:  'Before we leave ✦',
  night:   'The final night ✦',
  remains: 'What remains ✦',
};

const SectionEyebrow = ({ children, testId }) => (
  <p
    data-testid={testId}
    style={{
      margin: 0, fontFamily: FONT_BODY, fontSize: 11,
      letterSpacing: '0.22em', textTransform: 'uppercase',
      color: INK_FAINT,
    }}
  >
    {children}
  </p>
);

const resolveMedia = (rel) => {
  if (!rel) return null;
  if (rel.startsWith('http')) return rel;
  if (rel.startsWith('/api/')) return `${BACKEND_URL}${rel}`;
  return `${BACKEND_URL}/api/photos/${rel}`;
};

const ScenePreview = ({ scene }) => {
  if (scene.kind === 'still' && scene.photo) {
    return (
      <img
        data-testid="documentary-preview-still"
        alt=""
        src={resolveMedia(scene.photo.url || scene.photo.storage_path)}
        style={{
          display: 'block', width: '100%',
          aspectRatio: '16 / 9', objectFit: 'cover',
        }}
      />
    );
  }
  if (scene.kind === 'intertitle' || scene.kind === 'title' || scene.kind === 'closing') {
    return (
      <div
        data-testid="documentary-preview-intertitle"
        style={{
          aspectRatio: '16 / 9', background: '#0b0805', color: '#f0e8d2',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '0 18%', textAlign: 'center',
          fontFamily: FONT_DISPLAY, fontStyle: 'italic',
          fontSize: 'clamp(15px,1.6vw,22px)', lineHeight: 1.45,
        }}
      >
        {scene.text}
      </div>
    );
  }
  if (scene.kind === 'phase') {
    return (
      <div
        data-testid="documentary-preview-phase"
        style={{
          aspectRatio: '16 / 9', background: '#0b0805',
          color: 'rgba(245,239,222,0.84)',
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          fontFamily: FONT_DISPLAY, fontStyle: 'italic',
          fontSize: 'clamp(18px,2vw,26px)',
        }}
      >
        <div style={{
          width: 80, height: 1, background: GOLD,
          marginBottom: 16, opacity: 0.85,
        }} />
        {scene.text}
      </div>
    );
  }
  // black breath — render an almost-empty frame so the rhythm is legible.
  return (
    <div style={{
      aspectRatio: '16 / 9', background: '#070504',
    }} />
  );
};

const PhaseGroup = ({ phase, scenes }) => {
  if (!scenes.length) return null;
  return (
    <section data-testid={`documentary-preview-phase-${phase}`} style={{
      marginTop: 28,
    }}>
      <SectionEyebrow>{PHASE_LABELS[phase] || phase}</SectionEyebrow>
      <div style={{
        marginTop: 14, display: 'grid', gap: 8,
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
      }}>
        {scenes.map((s, i) => (
          <ScenePreview key={i} scene={s} />
        ))}
      </div>
    </section>
  );
};

const DocumentaryPage = () => {
  const { slug } = useParams();
  const [eventId, setEventId] = useState(null);
  const [eventName, setEventName] = useState(null);
  const [plan, setPlan] = useState(null);
  const [mp4Url, setMp4Url] = useState(null);
  // resolving | composing | gathering | ready | error
  const [status, setStatus] = useState('resolving');
  const [errorLine, setErrorLine] = useState('');

  useEffect(() => {
    if (!slug) return undefined;
    let aborted = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/events/slug/${slug}`);
        if (aborted) return;
        setEventId(r.data?.event_id);
        setEventName(r.data?.event_name || null);
        setStatus('idle');
      } catch {
        if (!aborted) {
          setStatus('error');
          setErrorLine('No evening found here ✦');
        }
      }
    })();
    return () => { aborted = true; };
  }, [slug]);

  const compose = useCallback(async () => {
    if (!eventId) return;
    setStatus('composing');
    setErrorLine('');
    try {
      // First arrival serves a cached artefact when the room state
      // hasn't changed. An explicit Regenerate ✦ press forces a
      // fresh encode — the brief's single allowed gesture.
      const force = status === 'ready' || status === 'gathering';
      const r = await axios.post(
        `${API}/events/${eventId}/documentary/compose`,
        null,
        { params: force ? { regenerate: true } : {} },
      );
      if (r.data?.status === 'settled' && r.data?.mp4_url) {
        setPlan(r.data.plan);
        setMp4Url(`${BACKEND_URL}${r.data.mp4_url}?t=${Date.now()}`);
        setStatus('ready');
      } else if (r.data?.status === 'gathering') {
        setPlan(r.data.plan || null);
        setMp4Url(null);
        setStatus('gathering');
      } else {
        setStatus('gathering');
      }
    } catch (e) {
      setStatus('error');
      setErrorLine(e?.response?.data?.detail || 'The room is gathering its voices ✦');
    }
  }, [eventId, status]);

  useEffect(() => {
    if (status === 'idle' && eventId) {
      compose();
    }
  }, [status, eventId, compose]);

  const statusLine = useMemo(() => {
    if (status === 'resolving') return 'Finding the evening ✦';
    if (status === 'composing') return 'The room is gathering its voices ✦';
    if (status === 'gathering') return 'The room is gathering its voices ✦';
    if (status === 'error')     return errorLine || 'The room is gathering its voices ✦';
    if (status === 'ready')     return 'The Documentary has settled ✦';
    return '';
  }, [status, errorLine]);

  // Group preview scenes by phase so the editorial spine reads as
  // three movements, not a flat scene list.
  const scenesByPhase = useMemo(() => {
    if (!plan?.scenes) return { before: [], night: [], remains: [] };
    const buckets = { before: [], night: [], remains: [] };
    plan.scenes.forEach((s) => {
      if (s.phase === 'before' || s.phase === 'night' || s.phase === 'remains') {
        buckets[s.phase].push(s);
      }
    });
    return buckets;
  }, [plan]);

  return (
    <div
      data-testid="documentary-page"
      data-status={status}
      style={{
        minHeight: '100vh', background: PARCHMENT, color: INK,
        fontFamily: FONT_BODY, padding: '24px 22px 80px',
      }}
    >
      <BackButton
        fallbackHref={`/event/${slug}`}
        label="Back"
        variant="on-light"
        testId="documentary-back-btn"
      />

      <header style={{ maxWidth: 760, margin: '40px auto 0' }}>
        <SectionEyebrow testId="documentary-eyebrow">
          PRESERVE THE NIGHT ✦  ·  OPERATOR
        </SectionEyebrow>
        <h1
          data-testid="documentary-title"
          style={{
            margin: '14px 0 0', fontFamily: FONT_DISPLAY, fontWeight: 500,
            fontSize: 'clamp(38px,5.4vw,56px)', lineHeight: 1.05,
            letterSpacing: '-0.015em', color: INK,
          }}
        >
          The Documentary ✦
        </h1>
        {eventName && (
          <p style={{
            margin: '10px 0 0', fontFamily: FONT_DISPLAY, fontStyle: 'italic',
            fontSize: 16, color: INK_SOFT,
          }}>{eventName}</p>
        )}
        <p
          data-testid="documentary-status"
          style={{
            margin: '22px 0 0', fontFamily: FONT_DISPLAY, fontStyle: 'italic',
            fontSize: 'clamp(17px,2vw,20px)', color: GOLD,
          }}
        >
          {statusLine}
        </p>

        {/* The brief permits ONE gesture: Regenerate ✦. Everything
            else would feel like editing software. */}
        <div style={{
          marginTop: 28, display: 'flex', gap: 12, flexWrap: 'wrap',
        }}>
          <button
            type="button"
            data-testid="documentary-regenerate-btn"
            onClick={compose}
            disabled={status === 'composing' || !eventId}
            style={{
              padding: '12px 22px', borderRadius: 4,
              border: `1px solid ${GOLD}`,
              background: status === 'composing' ? 'rgba(164,130,74,0.12)' : 'transparent',
              color: INK, fontFamily: FONT_DISPLAY, fontStyle: 'italic',
              fontSize: 15, letterSpacing: '0.02em',
              cursor: status === 'composing' || !eventId ? 'default' : 'pointer',
            }}
          >
            Regenerate ✦
          </button>
        </div>

        {/* When settled, the only thing the operator sees is the
            film itself. Full-bleed 16:9 video, native controls
            only (play / scrub / volume). No download CTA, no
            share button, no engagement language. */}
        {mp4Url && (
          <div
            data-testid="documentary-player"
            style={{
              marginTop: 32, background: '#0b0805',
              border: `1px solid ${RULE}`,
              boxShadow: '0 12px 30px rgba(31,20,9,0.18)',
            }}
          >
            <video
              data-testid="documentary-video"
              src={mp4Url}
              controls
              playsInline
              preload="metadata"
              style={{
                display: 'block', width: '100%',
                aspectRatio: '16 / 9', background: '#0b0805',
              }}
            />
          </div>
        )}
      </header>

      {/* Quiet editorial spine — the three movements as a sparse
          contact-sheet so the operator can read the shape of the
          documentary without scrubbing the video. No scene controls. */}
      {plan && plan.scenes && plan.scenes.length > 0 && (
        <main
          data-testid="documentary-preview"
          style={{
            maxWidth: 760, margin: '40px auto 0',
            background: IVORY, padding: '32px 28px',
            border: `1px solid ${RULE}`,
          }}
        >
          <PhaseGroup phase="before"  scenes={scenesByPhase.before} />
          <PhaseGroup phase="night"   scenes={scenesByPhase.night} />
          <PhaseGroup phase="remains" scenes={scenesByPhase.remains} />
        </main>
      )}
    </div>
  );
};

export default DocumentaryPage;
